import type { QuizQuestion } from "@/types/quiz";

export type AnswerMap = Record<string, number[]>;

function val(answers: AnswerMap, id: string): number {
  const arr = answers[id];
  if (!arr || !arr.length) return 0;
  return Math.max(...arr);
}

function sanitizeAndReplace(expr: string, answers: AnswerMap, ids: string[]): string | null {
  let out = expr;
  for (const id of ids) {
    const v = val(answers, id);
    out = out.replace(new RegExp(`\n`, "g"), " ");
    // Replace occurrences of exact question id with its numeric value
    // Use proper word boundaries to avoid partial replacements
    out = out.replace(new RegExp(`\\b${id.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, "g"), String(v));
  }
  if (!/^[-+*/%()\s\d<&>|=!.]+$/.test(out)) return null;
  return out;
}

export function evaluateCondition(expr: string | undefined, answers: AnswerMap, questions: QuizQuestion[]): boolean {
  if (!expr) return true;
  const ids = questions.map((q) => q.id);
  const sanitized = sanitizeAndReplace(expr, answers, ids);
  if (!sanitized) return false;
  try {
    // eslint-disable-next-line no-new-func
    const fn = new Function(`return (${sanitized});`);
    const res = fn();
    return Boolean(res);
  } catch {
    return false;
  }
}

export function computeScore(questions: QuizQuestion[], answers: AnswerMap): { score: number; max: number } {
  let sum = 0;
  let denom = 0;
  for (const q of questions) {
    const selected = answers[q.id] || [];
    const picked = q.multi_select ? (selected.length ? Math.max(...selected) : 0) : (selected[0] ?? 0);
    const maxOpt = q.options.reduce((m, o) => Math.max(m, Number(o.value || 0)), 0);
    sum += picked;
    denom += maxOpt;
  }
  const score = denom > 0 ? Math.round((sum / denom) * 100) : 0;
  return { score, max: 100 };
}
